import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

export const ReviewSectionCard = ({ title, icon, children, action, sx }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 2,
      overflow: "hidden",
      height: "100%",
      ...sx,
    }}
  >
    <CardHeader
      avatar={icon}
      title={title}
      action={action}
      titleTypographyProps={{
        variant: "subtitle1",
        fontWeight: 700,
        fontSize: { xs: "0.9375rem", sm: "1rem" },
      }}
      sx={{
        bgcolor: "grey.50",
        py: { xs: 1.25, sm: 1.5 },
        px: { xs: 1.5, sm: 2 },
        "& .MuiCardHeader-avatar": {
          color: "primary.main",
        },
        "& .MuiCardHeader-action": {
          m: 0,
          alignSelf: "center",
        },
      }}
    />
    <CardContent
      sx={{
        pt: { xs: 1.5, sm: 2 },
        px: { xs: 1.5, sm: 2 },
        pb: { xs: 1.5, sm: 2 },
        "&:last-child": { pb: { xs: 1.5, sm: 2 } },
      }}
    >
      {children}
    </CardContent>
  </Card>
);

ReviewSectionCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node,
  children: PropTypes.node.isRequired,
  action: PropTypes.node,
  sx: PropTypes.object,
};

export const ReviewField = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      display="block"
      sx={{ mb: 0.25, fontSize: { xs: "0.75rem", sm: "0.75rem" } }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={600}
      sx={{
        wordBreak: "break-word",
        fontSize: { xs: "0.8125rem", sm: "0.875rem" },
        lineHeight: 1.45,
      }}
    >
      {value != null && value !== "" ? value : "—"}
    </Typography>
  </Box>
);

ReviewField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]),
};

export const ReviewFieldGrid = ({ fields }) => (
  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
    {fields.map((field) => (
      <Grid item xs={12} sm={6} key={field.label}>
        <ReviewField label={field.label} value={field.value} />
      </Grid>
    ))}
  </Grid>
);

ReviewFieldGrid.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]),
    })
  ).isRequired,
};

export const ReviewDocumentStatus = ({ label, uploaded }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    spacing={1}
    sx={{
      py: 1,
      px: 1.25,
      borderRadius: 1.5,
      bgcolor: uploaded ? "rgba(46, 125, 50, 0.08)" : "grey.50",
      border: "1px solid",
      borderColor: uploaded ? "success.main" : "divider",
    }}
  >
    <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "0.8125rem", sm: "0.875rem" } }}>
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {uploaded ? (
        <>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 20 }} />
          <Typography variant="caption" color="success.dark" fontWeight={600}>
            بارگذاری شده
          </Typography>
        </>
      ) : (
        <>
          <HighlightOffOutlinedIcon color="disabled" sx={{ fontSize: 20 }} />
          <Typography variant="caption" color="text.secondary">
            ناموجود
          </Typography>
        </>
      )}
    </Stack>
  </Stack>
);

ReviewDocumentStatus.propTypes = {
  label: PropTypes.string.isRequired,
  uploaded: PropTypes.bool.isRequired,
};

export const ReviewStatusChip = ({ label, color = "default", variant = "filled" }) => (
  <Chip label={label} color={color} size="small" variant={variant} sx={{ fontWeight: 600 }} />
);

ReviewStatusChip.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.string,
  variant: PropTypes.string,
};
